import GameSignals from '@/signals/GameSignals'
import { BaseChildState } from '@stateMachine/BaseChildState'
import gsap from 'gsap'
import Facade from '@/Facade'
import { getFreeSpinsFinalWinAmount } from '@/states/freespins/FreeSpinsFinalWin'
import { toMajorUnits } from '@/utils/CurrencyUnits'

export class FreeSpinsSummaryState extends BaseChildState {
    //## Matches MaxExposureState — the teardown is parked until the popup answers.
    private static readonly MAX_EXPOSURE_FALLBACK_MS: number = 8000
    private static readonly BONUS_END_UNLOCK_CAP_MS: number = 100

    private _transitionFallbackTimeoutId: number = null
    private _isTransitionHandled: boolean = false
    private _isCompleted: boolean = false
    private _maxExposureFallbackTimeoutId: number = null
    private _isWaitingMaxExposure: boolean = false

    public canTrigger(): boolean {
        const isFreeSpinsActive = Boolean(this._model.roundState.isFreeSpins)
        const isHoldAndWinActive = Boolean(this._model.spinState.isHoldAndWinActive)
        const freebetAmountLeft = Number(this._model.spinData?.freebet?.amount_left ?? -1)
        const freebetTotalGiven = Number(this._model.spinData?.freebet?.total_amount_given ?? 0)
        const nextRoundType = String(this._model.spinData?.next_round_type || '').toLowerCase()
        const isEnteringHoldAndWin = nextRoundType === 'respin' || nextRoundType === 'respins'
        const isHoldAndWinFlowActive = Boolean(
            this._model.spinState.isHoldAndWinActive ||
                (Facade as any).holdAndWinCollectInProgress ||
                (Facade as any).holdAndWinPendingBaseTransition
        )

        return (
            isFreeSpinsActive &&
            !isHoldAndWinActive &&
            Number.isFinite(freebetTotalGiven) &&
            freebetTotalGiven > 0 &&
            freebetAmountLeft === 0 &&
            !isEnteringHoldAndWin &&
            !isHoldAndWinFlowActive
        )
    }

    public begin() {
        ;(Facade as any).skipFinalWinPresentationAfterHoldAndWin = false
        // H&W summary already consumed on the path that routes here — keep it consumed so
        // HoldAndWinSummary cannot re-open after this popup.
        ;(Facade as any).holdAndWinSummaryConsumed = true
        GameSignals.showFreeSpinsSummaryComplete.connect(this.onSummaryComplete)
        this._isCompleted = false

        gsap.to(this, {
            duration: 0.5,
            onComplete: () => {
                // Credit Win Meter and balance in the same beat as the popup (not during collect /
                // commendation), so the jackpot-ladder prize lands with the Congratulations screen.
                this.commitFreeSpinsWinToMeter()
                // GameSignals.showFreeSpinsSummary.emit(this._model.winCounter.totalWin) [commented by chaitanya, sometime this._model.winCounter.totalWin getting 0]
                GameSignals.showFreeSpinsSummary.emit(this.getFsTotalWinAmount())
            },
        })
    }

    private commitFreeSpinsWinToMeter(): void {
        const finalWinAmount = this.getFsTotalWinAmount()
        if (!(finalWinAmount > 0)) {
            return
        }

        ;(GameSignals as any).setWinPanelValue?.emit(toMajorUnits(finalWinAmount))
        GameSignals.balanceHasChanged.emit(this._model.balance)
    }

    private getFsTotalWinAmount(): number {
        return getFreeSpinsFinalWinAmount(this._model)
    }

    private onSummaryComplete = () => {
        if (this._isCompleted) {
            return
        }

        this._isCompleted = true

        // Capped round: show the notice here, over the free spins scene, instead of letting
        // MaxExposureState run it after the base-game transition — that lands ~2.5s later.
        // MaxExposureState still covers base game / Hold & Win caps, where this never runs.
        if (this.shouldShowMaxExposure()) {
            this.playMaxExposureThenTeardown()
            return
        }

        this.runBonusEndTeardown()
    }

    private shouldShowMaxExposure(): boolean {
        return !this._model.isMaxExposureShown && Boolean(this._model.spinData?.cap_amount_reached)
    }

    private playMaxExposureThenTeardown(): void {
        // Set before the emit: the popup can answer synchronously when it has no spine.
        this._model.isMaxExposureShown = true
        this._isWaitingMaxExposure = true

        Facade.isPopupBlockingInput = true
        ;(GameSignals as any).maxExposureComplete?.connect(this.onMaxExposureComplete)

        GameSignals.stopAutoplay.emit()
        ;(GameSignals as any).showMaxExposure?.emit()

        this._maxExposureFallbackTimeoutId = window.setTimeout(
            this.onMaxExposureComplete,
            FreeSpinsSummaryState.MAX_EXPOSURE_FALLBACK_MS
        )
    }

    private onMaxExposureComplete = () => {
        if (!this._isWaitingMaxExposure) {
            return
        }

        this._isWaitingMaxExposure = false
        this.clearMaxExposureWait()
        Facade.isPopupBlockingInput = false

        this.runBonusEndTeardown()
    }

    private clearMaxExposureWait(): void {
        if (this._maxExposureFallbackTimeoutId !== null) {
            clearTimeout(this._maxExposureFallbackTimeoutId)
            this._maxExposureFallbackTimeoutId = null
        }

        ;(GameSignals as any).maxExposureComplete?.disconnect(this.onMaxExposureComplete)
    }

    private runBonusEndTeardown(): void {
        this._isTransitionHandled = false
        GameSignals.completeTransitionAnimation.connect(this.onBonusEndTransitionComplete)

        this._model.isBonusGameNow = false
        this._model.roundState.isFreeSpins = false

        // Original flow: switch base UI (bg / logo / jackpots) together with the wave.
        // Emit showBaseGameBackground only here — a second emit on wave complete caused the jerk.
        GameSignals.showBaseGameBackground.emit()
        GameSignals.bonusStop.emit(this._model.winCurrency)

        // Safety fallback in case transition animation does not emit completion
        this._transitionFallbackTimeoutId = window.setTimeout(
            this.onBonusEndTransitionComplete, FreeSpinsSummaryState.BONUS_END_UNLOCK_CAP_MS)    
        }

    private onBonusEndTransitionComplete = () => {
        if (this._isTransitionHandled) {
            return
        }

        this._isTransitionHandled = true

        if (this._transitionFallbackTimeoutId !== null) {
            clearTimeout(this._transitionFallbackTimeoutId)
            this._transitionFallbackTimeoutId = null
        }

        GameSignals.completeTransitionAnimation.disconnect(this.onBonusEndTransitionComplete)

        const timeline = gsap.timeline()
        timeline
            .to(this, {
                duration: 0.1,
                onComplete: () => {
                    GameSignals.bonusStopComplete.emit()
                    GameSignals.unLockGamePanel.emit()
                },
            })
            .to(this, {
                duration: 0.2,
                onComplete: () => {
                    gsap.killTweensOf(this)
                    this.end()
                },
            })
    }

    public cleanUp() {
        if (this._transitionFallbackTimeoutId !== null) {
            clearTimeout(this._transitionFallbackTimeoutId)
            this._transitionFallbackTimeoutId = null
        }

        if (this._isWaitingMaxExposure) {
            this._isWaitingMaxExposure = false
            Facade.isPopupBlockingInput = false
        }

        this.clearMaxExposureWait()

        GameSignals.showFreeSpinsSummaryComplete.disconnect(this.onSummaryComplete)
        GameSignals.completeTransitionAnimation.disconnect(this.onBonusEndTransitionComplete)
    }
}
